// @flow

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { getRouteParams, getCurrentPath } from "@selectors";
import {
  Loader,
  View,
  Text,
  SectionComponent,
  StatItem,
  Button,
  TooltipDropdown,
  StatTileGroup,
} from "@components";
import {
  getEmployee,
  isLoading,
  employeeReferralHistory,
  getActionSummary,
} from "@modules/employee/selectors";
import { hasPermission } from "@modules/account/selectors";
import { isLoading as isReportingLoading } from "@modules/reporting/selectors/referral-tracker";
import employeeActionCreators from "@modules/employee/actionCreator";
import type { EmployeeActionSummaryType, EmployeeType } from "@modules/employee/types";
import withErrorHandler from "@components/error";
import { withClientContext, type PropsFromClientContext } from "@modules/context/components/";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import {
  DeclinedReferralHistoryGrid,
  ManualReferralModal,
  ReferralHistoryGrid,
  WellbeingReferralDetailsModal,
  WellbeingReferralLogModal,
} from "@modules/shared/components";
import * as clientsSelectors from "@modules/client/selectors";
import * as selectors from "@modules/shared/selectors";
import actionCreator from "@modules/shared/actionCreator";
import type { EmployeeReferralHistory } from "@modules/employee/reducer/employee/types";
import {
  showLogReferralDetailsModal,
  showReferralDetailsModal,
} from "@modules/reporting/selectors/referral-tracker";
import type { ClientServiceAbsenceManagement } from "@modules/client/reducer/service/types";

type PropsFromState = $Exact<{
  currentPath: string,
  isLoading: boolean,
  isManualReferralAvailable: boolean,
  isManualEligible: boolean,
  showManualReferralModal: boolean,
  employeeId: number,
  employee: EmployeeType,
  employeeReferralHistory: EmployeeReferralHistory,
  actionSummary?: {
    numberToManager: string,
    values: Array<EmployeeActionSummaryType>,
  } | null,
  showWellbeingReferralDetailsModal: boolean,
  showWellbeingReferralLogModal: boolean,
  service: ClientServiceAbsenceManagement,
  shouldAccessReferral: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getEmployees: typeof employeeActionCreators.GetEmployees,
  toggleManualReferralModal: typeof actionCreator.ToggleManualReferralModal,
  getEmployeeReferralHistory: typeof employeeActionCreators.GetEmployeeReferralHistory,
  exportReferralHistory: typeof employeeActionCreators.ExportEmployeeReferralHistory,
  exportDeclinedReferralHistory: typeof employeeActionCreators.ExportEmployeeDeclinedReferralHistory,
  getActionSummary: typeof employeeActionCreators.GetEmployeeActionSummary,
  getManualReferralEnabled: typeof actionCreator.GetManualReferralEnabled,
  getReferralProcesses: typeof actionCreator.GetReferralProcesses,
}>;

type Props = $Exact<{
  ...PropsFromPermissions,
  ...PropsFromClientContext,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export function EmployeeReferrals(props: Props) {
  const isReferalTrackingEnabled = props.service?.settings?.referralTracking;
  const message = isReferalTrackingEnabled
    ? "EMPLOYEE_REFERRALS_TAB_INFO"
    : "EMPLOYEE_REFERRALS_TAB_NO_SERVICE";

  useEffect(() => {
    props.getEmployees({
      clientId: props.clientId,
      filters: {
        id: props.employeeId,
      },
    });

    props.getEmployeeReferralHistory({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });

    props.getActionSummary({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });

    props.getManualReferralEnabled({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });
    props.getReferralProcesses({
      clientId: props.clientId,
    });
  }, []);

  function exportReferralHistory() {
    props.exportReferralHistory({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });
  }

  function exportDeclinedReferralHistory() {
    props.exportDeclinedReferralHistory({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });
  }

  const taskStatData = [
    {
      title: props.t("TOTAL_REFERRALS"),
      value: props.employeeReferralHistory.totalReferrals || 0,
      className: "bg-light-grey",
    },
    {
      title: props.t("MANAGER_REFERRALS"),
      value: props.employeeReferralHistory.managerLedReferrals || 0,
    },
    {
      title: props.t("EMPLOYEE_REFERRALS"),
      value: props.employeeReferralHistory.employeeLedReferrals || 0,
    },
    {
      title: props.t("MANUAL_REFERRALS"),
      value: props.employeeReferralHistory.manualReferrals || 0,
    },
    {
      title: (
        <div
          className={"text-color-pumpkin-orange"}
          dangerouslySetInnerHTML={{
            __html: props.t("NEED_TO_CONTACT_EMPLOYEE_NAME", {
              name: (props.employee || {}).firstName,
            }),
          }}
        />
      ),
      className: "bg-light-coffee",
      value: (
        <p className="font-size-14 mt-1">
          <span>
            <span>{props.t("ABSENCE_HISTORY_CONTACT_EMPLOYEE_TEXT_1")}</span>
            <b>{(props.actionSummary && props.actionSummary.numberToManager) || ""}</b>
            <span>{props.t("ABSENCE_HISTORY_CONTACT_EMPLOYEE_TEXT_2")}</span>
          </span>
        </p>
      ),
    },
  ];

  if (!props.employeeReferralHistory) return <div />;
  return (
    <View position="relative" gap={8}>
      <Text className="message-box" text={props.t(message)} />

      {isReferalTrackingEnabled && (
        <View gap={6}>
          <StatTileGroup data={taskStatData} />

          <SectionComponent
            wrapperClassName="flex-column"
            title={<h5>{props.t("ACCEPTED_REFERRAL")}</h5>}
            rightLink={
              <View direction="row">
                {props.employeeReferralHistory.employeeWellbeingReferrals.length > 0 && (
                  <Button className="ml-2" onClick={exportReferralHistory}>
                    {props.t("EXPORT_ALL")}
                  </Button>
                )}
                {props.shouldAccessReferral && props.isManualReferralAvailable && (
                  <TooltipDropdown
                    id="manual-referral"
                    description={props.t("EMPLOYEE_REFERRAL_WARNING_TOOLTIP_TEXT")}
                    hidden={props.isManualEligible}
                  >
                    <Button
                      className="ml-2"
                      disabled={!props.isManualEligible}
                      onClick={props.toggleManualReferralModal}
                    >
                      {props.t("MANUAL_REFERRAL")}
                    </Button>
                  </TooltipDropdown>
                )}
              </View>
            }
            className="gs-container"
          >
            <div className="d-flex justify-content-end w-100">
              {props.showManualReferralModal && (
                <ManualReferralModal
                  t={props.t}
                  clientId={props.clientId}
                  employeeId={props.employeeId}
                />
              )}
            </div>
            <ReferralHistoryGrid
              clientId={props.clientId}
              data={props.employeeReferralHistory.employeeWellbeingReferrals}
            />
            {props.showWellbeingReferralDetailsModal && (
              <WellbeingReferralDetailsModal clientId={props.clientId} />
            )}
            {props.showWellbeingReferralLogModal && (
              <WellbeingReferralLogModal clientId={props.clientId} />
            )}
          </SectionComponent>

          <SectionComponent
            wrapperClassName="flex-column"
            className="gs-container"
            title={<h5>{props.t("DECLINED_REFERRAL")}</h5>}
            rightLink={
              <div>
                {props.employeeReferralHistory.employeeDeclinedWellbeingReferrals.length > 0 && (
                  <Button className="ml-2" onClick={exportDeclinedReferralHistory}>
                    {props.t("EXPORT_ALL")}
                  </Button>
                )}
              </div>
            }
          >
            <DeclinedReferralHistoryGrid
              data={props.employeeReferralHistory.employeeDeclinedWellbeingReferrals}
            />
          </SectionComponent>
        </View>
      )}
    </View>
  );
}

const mapStateToProps = (state: Object, ownProps): PropsFromState => {
  const routeParams = getRouteParams(ownProps);
  const employeeId = parseInt(routeParams.employeeId);

  return {
    isLoading: isLoading(state) || isReportingLoading(state),
    currentPath: getCurrentPath(state),
    employee: getEmployee(employeeId)(state),
    isManualReferralAvailable: selectors.isManualReferralAvailable(state),
    isManualEligible: selectors.isManualEmployeeEligible(state),
    showManualReferralModal: selectors.showManualReferralModal(state),
    employeeReferralHistory: employeeReferralHistory(state),
    actionSummary: getActionSummary(state),
    employeeId,
    showWellbeingReferralDetailsModal: showReferralDetailsModal(state),
    showWellbeingReferralLogModal: showLogReferralDetailsModal(state),
    service: clientsSelectors.getService(state),
    shouldAccessReferral: hasPermission(state, PermissionEnum.PRIVILEGE_MANUAL_REFERRAL_WRITE),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getEmployees: employeeActionCreators.GetEmployees,
  getEmployeeReferralHistory: employeeActionCreators.GetEmployeeReferralHistory,
  exportReferralHistory: employeeActionCreators.ExportEmployeeReferralHistory,
  exportDeclinedReferralHistory: employeeActionCreators.ExportEmployeeDeclinedReferralHistory,
  toggleManualReferralModal: actionCreator.ToggleManualReferralModal,
  getActionSummary: employeeActionCreators.GetEmployeeActionSummary,
  getManualReferralEnabled: actionCreator.GetManualReferralEnabled,
  getReferralProcesses: actionCreator.GetReferralProcesses,
};

export default compose(
  withErrorHandler(),
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withClientContext(),
  withPermissions({ permissionId: PermissionEnum.ACCESS_REPORTING_EMPLOYEES }),
)(EmployeeReferrals);
